import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLightbox } from "@/lib/lightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EditProfileCard } from "@/components/EditProfileCard";
import { toast } from "sonner";
import { Upload, CheckCircle2, Clock, XCircle, AlertCircle, Trash2, ImageIcon, ShieldCheck } from "lucide-react";

async function uploadPrivate(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("scope", "private");
  fd.append("kind", kind);
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.path;
}

export default function Verify() {
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") || "";
  const isPublishIntent = from.startsWith("/publish") || from.startsWith("/publicar");
  // Store-only fields (RUC + bank cert)
  const [rucDoc, setRucDoc] = useState(null);
  const [bankCert, setBankCert] = useState(null);
  // Person light-KYC fields (new — replaces cedula photos + selfie)
  const [cedula, setCedula] = useState(user?.cedula || "");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const isStore = user?.account_type === "store";

  // Ecuadorian cédula validation (modulus-10) — matches backend
  const isValidCedula = (c) => {
    const v = (c || "").trim();
    if (!/^\d{10}$/.test(v)) return false;
    const prov = parseInt(v.slice(0, 2), 10);
    if (!((prov >= 1 && prov <= 24) || prov === 30)) return false;
    if (parseInt(v[2], 10) >= 6) return false;
    const w = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    let total = 0;
    for (let i = 0; i < 9; i++) {
      let p = parseInt(v[i], 10) * w[i];
      if (p > 9) p -= 9;
      total += p;
    }
    return ((10 - (total % 10)) % 10) === parseInt(v[9], 10);
  };

  const submitLight = async (e) => {
    e.preventDefault();
    if (!isValidCedula(cedula)) { toast.error("Número de cédula inválido"); return; }
    setLoading(true);
    try {
      await api.post("/verification/person-light", {
        cedula: cedula.trim(),
      });
      toast.success("¡Verificado! Ya puedes publicar y vender cartas.");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const submitStore = async (e) => {
    e.preventDefault();
    if (!rucDoc || !bankCert) { toast.error("Sube documento del RUC (SRI) y certificado bancario"); return; }
    setLoading(true);
    try {
      const [rucPath, bankPath] = await Promise.all([
        uploadPrivate(rucDoc, "ruc_doc"),
        uploadPrivate(bankCert, "bank_cert"),
      ]);
      await api.post("/verification/submit", {
        // Backwards-compat: the endpoint still requires these three fields.
        // For stores we send the RUC + bank paths as sentinels — backend
        // only enforces RUC + bank for store accounts.
        cedula_photo_path: rucPath,
        cedula_back_photo_path: rucPath,
        selfie_photo_path: bankPath,
        ruc_document_path: rucPath,
        bank_cert_photo_path: bankPath,
      });
      toast.success("Documentos enviados. El administrador revisará pronto.");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally { setLoading(false); }
  };

  const status = user?.verification_status;

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-4">VERIFICACIÓN DE IDENTIDAD</h1>

      {/* Contextual intent banner — only shown to would-be SELLERS. Buyers no
          longer need photo verification; they enter their cédula at checkout. */}
      {isPublishIntent && (status === "pendiente_documentos" || status === "rechazado") && (
        <div className="p-4 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-md text-sm text-[#FCA5A5]" data-testid="verify-intent-banner">
          📤 <b>Sube tu cédula para publicar tu carta.</b> Verificamos la identidad de todos los vendedores para proteger a los compradores. Es un paso único.
        </div>
      )}

      {/* Optional-until-action explainer for the flow WITHOUT publish intent.
          Explicitly tells the user they DON'T need this to buy — only to sell. */}
      {!isPublishIntent && (status === "pendiente_documentos" || status === "rechazado") && (
        <div className="p-3 mb-4 bg-neutral-500/10 border border-neutral-700 rounded-md text-xs text-neutral-400" data-testid="verify-optional-banner">
          Para <b>comprar</b> no necesitas subir cédula — solo ingresas tu número en el checkout. Este proceso con fotos y selfie es para <b>publicar y vender cartas</b>. Si aún no vas a vender, puedes ignorarlo por ahora.
        </div>
      )}

      <div className={`p-4 rounded-md border mb-6 flex items-start gap-3 ${
        status === "aprobado" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" :
        status === "rechazado" ? "bg-red-500/10 border-red-500/30 text-red-300" :
        status === "pendiente_revision" ? "bg-amber-500/10 border-amber-500/30 text-amber-300" :
        "bg-neutral-500/10 border-neutral-700 text-neutral-300"
      }`}>
        {status === "aprobado" && <CheckCircle2 size={20} />}
        {status === "rechazado" && <XCircle size={20} />}
        {status === "pendiente_revision" && <Clock size={20} />}
        {status === "pendiente_documentos" && <AlertCircle size={20} />}
        <div>
          <div className="font-semibold" data-testid="verify-status">Estado: {status}</div>
          <div className="text-sm opacity-80 mt-1">
            {status === "aprobado" && "Ya puedes publicar y vender cartas."}
            {status === "rechazado" && "Tu verificación fue rechazada. Sube nuevos documentos."}
            {status === "pendiente_revision" && "Documentos recibidos. Un admin revisará en breve."}
            {status === "pendiente_documentos" && "Para VENDER cartas: sube frente y reverso de tu cédula + una selfie sosteniendo la cédula. Para COMPRAR no necesitas esto."}
          </div>
        </div>
      </div>

      {(status === "pendiente_documentos" || status === "rechazado") && !isStore && (
        <>
          <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/40 rounded-md text-sm text-blue-100 leading-relaxed" data-testid="light-verify-explainer">
            <div className="flex items-start gap-2">
              <ShieldCheck size={18} className="mt-0.5 shrink-0" />
              <div>
                <b>Verificación ligera — sin fotos.</b> Solo necesitamos tu número de cédula ecuatoriana. Con eso quedas verificado al instante y puedes publicar y vender.
              </div>
            </div>
          </div>
          <form onSubmit={submitLight} className="space-y-4 bg-[#111] border border-[#262626] rounded-md p-6" data-testid="light-verify-form">
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Número de cédula</Label>
              <Input
                data-testid="light-verify-cedula"
                inputMode="numeric"
                pattern="\d*"
                value={cedula}
                onChange={(e) => setCedula(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10 dígitos"
                className={`bg-[#171717] border-[#262626] text-white font-mono ${cedula && !isValidCedula(cedula) ? "border-red-500" : ""}`}
                required
              />
              {cedula && !isValidCedula(cedula) && (
                <div className="text-[11px] text-red-400 mt-1">Número inválido — revisa que sea una cédula ecuatoriana correcta.</div>
              )}
            </div>
            <Button data-testid="light-verify-submit" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
              <BusyLabel busy={loading} text="Verificando…">Verificar cuenta</BusyLabel>
            </Button>
            <div className="text-[11px] text-neutral-500 leading-relaxed">
              🔒 Tus datos se guardan cifrados y solo se usan para confirmar tu identidad ante el administrador. No pedimos fotos ni selfie salvo que haya una disputa activa contigo.
            </div>
          </form>
        </>
      )}

      {(status === "pendiente_documentos" || status === "rechazado") && isStore && (
        <>
        <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/40 rounded-md text-sm text-purple-100 leading-relaxed">
          🏢 <b>Cuenta TIENDA.</b> Necesitamos el <b>documento del RUC (SRI)</b> y un <b>certificado bancario</b> (PDF o foto). La cuenta bancaria puede estar a nombre del RUC/negocio o del dueño personal. El admin valida manualmente.
        </div>
        <form onSubmit={submitStore} className="space-y-6 bg-[#111] border border-[#262626] rounded-md p-6">
          <ImageField
            label="Documento del RUC (SRI) — obligatorio"
            file={rucDoc} setFile={setRucDoc}
            testid="verify-ruc-doc-file"
            hint="El PDF que descargas del SRI o una foto legible"
            acceptPdf
          />
          <ImageField
            label="Certificado bancario — obligatorio"
            file={bankCert} setFile={setBankCert}
            testid="verify-bank-cert-file"
            hint="Emitido por tu banco. Puede estar a nombre del RUC/negocio o del dueño personal. PDF o foto."
            acceptPdf
          />
          <Button data-testid="verify-submit" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
            <BusyLabel busy={loading} text="Subiendo…">Enviar para verificación</BusyLabel>
          </Button>
        </form>
        </>
      )}
      {status === "aprobado" && (
        <div className="mb-6">
          <Button onClick={() => nav("/publish")} className="bg-[#EF4444] hover:bg-[#DC2626]">Publicar mi primera carta</Button>
        </div>
      )}

      {/* Perfil editable — visible SIEMPRE, sin importar el estado de verificación */}
      <div className="mt-6">
        <EditProfileCard />
      </div>
    </div>
  );
}

function isHeic(file) {
  if (!file) return false;
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif") || type === "image/heic" || type === "image/heif";
}

function isPdf(file) {
  if (!file) return false;
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return name.endsWith(".pdf") || type === "application/pdf";
}

function ImageField({ label, file, setFile, testid, hint, acceptPdf = false }) {
  const [preview, setPreview] = useState(null);
  const [converting, setConverting] = useState(false);
  const { open: openLightbox } = useLightbox();
  const filePdf = isPdf(file);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    if (!file) { setPreview(null); return; }
    // PDFs open in a new tab — no in-page thumbnail is generated.
    if (isPdf(file)) {
      createdUrl = URL.createObjectURL(file);
      setPreview(createdUrl);
      return () => { if (createdUrl) URL.revokeObjectURL(createdUrl); };
    }
    (async () => {
      try {
        let blob = file;
        if (isHeic(file)) {
          setConverting(true);
          const { heicTo } = await import("heic-to");
          blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.8 });
        }
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPreview(createdUrl);
      } catch (err) {
        console.error("HEIC preview failed:", err);
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setConverting(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [file]);

  const accept = acceptPdf ? "image/*,.heic,.heif,application/pdf,.pdf" : "image/*,.heic,.heif";
  const formatsLine = acceptPdf ? "JPG / PNG / WEBP / HEIC / PDF (máx 15 MB)" : "JPG / PNG / WEBP / HEIC (máx 15 MB)";
  const uploadLine = acceptPdf ? "Click para subir imagen o PDF" : "Click para subir imagen";

  return (
    <div>
      <label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">{label}</label>
      {file ? (
        <div className="flex gap-3 items-start p-3 bg-[#0A0A0A] border border-[#262626] rounded-md">
          {filePdf ? (
            <div
              onClick={() => preview && window.open(preview, "_blank", "noopener")}
              data-testid={`${testid}-preview`}
              className="w-28 h-28 flex flex-col items-center justify-center rounded border border-[#262626] bg-red-500/5 text-red-300 cursor-pointer hover:border-[#EF4444] transition"
              title="Abrir PDF en pestaña nueva"
            >
              <div className="font-mono font-bold text-2xl">PDF</div>
              <div className="text-[10px] mt-1">Click para ver</div>
            </div>
          ) : preview ? (
            <img
              src={preview}
              alt="preview"
              data-testid={`${testid}-preview`}
              onClick={() => openLightbox([{ url: preview }])}
              className="w-28 h-28 object-cover rounded border border-[#262626] cursor-zoom-in"
            />
          ) : (
            <div className="w-28 h-28 flex items-center justify-center rounded border border-[#262626] bg-[#0A0A0A] text-neutral-500 text-[10px] text-center px-2">
              {converting ? "Convirtiendo HEIC…" : "Vista previa no disponible"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{file.name}</div>
            <div className="text-xs text-neutral-500 mt-1">{(file.size / 1024).toFixed(0)} KB{filePdf && <span className="ml-2 text-red-300">· PDF</span>}</div>
            {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
            <button
              type="button"
              onClick={() => setFile(null)}
              data-testid={`${testid}-remove`}
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#EF4444] hover:underline"
            >
              <Trash2 size={12} /> Quitar
            </button>
          </div>
        </div>
      ) : (
        <label className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-dashed border-[#262626] rounded-md cursor-pointer hover:border-[#EF4444] transition-colors">
          <Upload size={18} className="text-neutral-500" />
          <div className="flex-1">
            <div className="text-sm text-neutral-300">{uploadLine}</div>
            {hint && <div className="text-xs text-neutral-500 mt-0.5">{hint}</div>}
            <div className="text-[10px] text-neutral-600 mt-1">{formatsLine}</div>
          </div>
          <ImageIcon size={16} className="text-neutral-600" />
          <input
            data-testid={testid}
            type="file"
            accept={accept}
            onChange={(e) => setFile(e.target.files?.[0])}
            className="hidden"
          />
        </label>
      )}
    </div>
  );
}
